import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MapGL, { 
  GeolocateControl, 
  NavigationControl
} from 'react-map-gl';
import { motion } from 'framer-motion';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useNativeVenueLayer } from './NativeVenueLayer';
import DirectionalArrow from './DirectionalArrow';
import VenueDrawer from './VenueDrawer';
import { REGION_CONFIG, getApiBaseUrl } from '../../config/region';
import { useTranslation } from 'react-i18next';
import Header from '../Common/Header';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Cache constants for instant loads
const CACHE_KEY = 'honestlee_venues_cache';
const CACHE_EXPIRY_KEY = 'honestlee_cache_expiry';
const CACHE_AREAS_KEY = 'honestlee_loaded_areas';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// ========================================
// UTILITY FUNCTIONS
// ========================================

// Extract coordinates from venue (Neon API: flat lat/lng)
const getVenueCoordinates = (venue) => {
  const lat = parseFloat(venue.lat);
  const lng = parseFloat(venue.lng);

  return { lat, lng, isValid: !isNaN(lat) && !isNaN(lng) && lat && lng };
};

// ========================================
// MAIN COMPONENT
// ========================================

const VenueMap = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mapRef = useRef();
  
  // ========================================
  // STATE
  // ========================================
  
  const [venues, setVenues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const loadedAreasRef = useRef(new Set()); // ✅ FIXED: Use ref instead of state for better caching
  const [isInitialLoad, setIsInitialLoad] = useState(true); // ✅ FIX: Prevent duplicate loads
  
  // Map viewport state - start ZOOMED IN if GPS available
  const [viewState, setViewState] = useState({
    longitude: REGION_CONFIG.mapCenter.longitude,
    latitude: REGION_CONFIG.mapCenter.latitude,
    zoom: 12 // Will update to 15-16 when GPS detected
  });

  // ========================================
  // FETCH VENUES (GPS + Proximity Based)
  // ========================================
  
  const fetchNearbyVenues = useCallback(async (lat, lng, radiusMeters = 3000) => {
    try {
      setIsLoading(true);
      const API_BASE_URL = getApiBaseUrl();
      
      // Create area key for caching
      const areaKey = `${Math.floor(lat * 100)},${Math.floor(lng * 100)}`;
      
      // ✅ FIXED: Use ref for caching (skip if already loaded)
      if (loadedAreasRef.current.has(areaKey)) {
        console.log('✅ Cache HIT:', areaKey, '- SKIPPING API CALL');
        setIsLoading(false);
        return;
      }
      
      console.log('📥 Cache MISS:', areaKey, '- LOADING NEW AREA');
      
      // ✅ FIX: Get auth token for API requests
      const token = localStorage.getItem('venue_app_token') || 
                    localStorage.getItem('token') || 
                    localStorage.getItem('authToken');
      
      // Limit scales with radius — zoomed out needs more results
      const limitForRadius = radiusMeters >= 500000 ? 2500 : radiusMeters >= 50000 ? 1000 : 200;
      const url = `${API_BASE_URL}/venues?latitude=${lat}&longitude=${lng}&radius=${radiusMeters}&limit=${limitForRadius}`;
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': REGION_CONFIG.region,
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('Failed to fetch venues:', response.status);
        setIsLoading(false);
        return;
      }
      
      const data = await response.json();
      const newVenues = data.venues || data.data || [];

      // 🔍 DEBUG: Show Neon vs Mongo source in console
      if (newVenues.length > 0) {
        const src = newVenues[0]._source || 'mongo';
        const neonCount = newVenues.filter(v => v._source === 'neon').length;
        const mongoCount = newVenues.length - neonCount;
        console.group(`🗺️  Map venues loaded — radius=${radiusMeters}m`);
        console.log(`  📦 Source: ${src} | Total: ${newVenues.length}`);
        console.log(`  🐘 Neon (PostGIS): ${neonCount} venues`);
        if (mongoCount > 0) console.log(`  🍃 MongoDB fallback: ${mongoCount} venues`);
        if (newVenues[0]._dist_m != null) console.log(`  📍 Nearest: "${newVenues[0].name}" at ${newVenues[0]._dist_m}m`);
        console.groupEnd();
      }

      // ✅ FIXED: Mark area as loaded using ref
      loadedAreasRef.current.add(areaKey);

      setVenues(prev => {
        const venueMap = new Map();
        [...prev, ...newVenues].forEach(v => {
          const id = v._id || String(v.id) || v.Dubai_id;
          if (id) {
            venueMap.set(id, v);
          }
        });
        const merged = Array.from(venueMap.values());
        setLoadedCount(merged.length);
        console.log(`✅ Loaded ${newVenues.length} new venues (${merged.length} total cached)`);
        return merged;
      });
      
      // ✅ FIXED: Update total count using functional setState
      setTotalCount(current => current || data.pagination?.total || 0);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching venues:', error);
      setIsLoading(false);
    }
  }, []); // ✅ FIXED: Empty dependencies to avoid recreating

  // ========================================
  // LOAD CACHE ON MOUNT (Instant Load)
  // ========================================

  useEffect(() => {
    const loadCache = () => {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
        const cachedAreas = localStorage.getItem(CACHE_AREAS_KEY);
        
        if (cached && expiry) {
          const now = Date.now();
          if (now < parseInt(expiry)) {
            const cachedVenues = JSON.parse(cached);
            if (cachedVenues.length === 0) {
              // Empty cache is stale — discard it so fresh data loads
              console.log('⚠️ Cache is empty, discarding to force fresh load');
              localStorage.removeItem(CACHE_KEY);
              localStorage.removeItem(CACHE_EXPIRY_KEY);
              localStorage.removeItem(CACHE_AREAS_KEY);
              return false;
            }
            console.log(`✅ INSTANT LOAD: ${cachedVenues.length} venues from cache`);
            setVenues(cachedVenues);
            setLoadedCount(cachedVenues.length);

            // Restore loaded areas
            if (cachedAreas) {
              const areas = JSON.parse(cachedAreas);
              loadedAreasRef.current = new Set(areas);
            }

            return true;
          } else {
            console.log('⏰ Cache expired (24hrs) - fetching fresh');
            localStorage.removeItem(CACHE_KEY);
            localStorage.removeItem(CACHE_EXPIRY_KEY);
            localStorage.removeItem(CACHE_AREAS_KEY);
          }
        }
      } catch (error) {
        console.error('❌ Cache load failed:', error);
      }
      return false;
    };
    
    loadCache();
  }, []); // Run once on mount

  // ========================================
  // INITIAL LOAD (GPS-Based, Zoomed In)
  // ========================================
  
  useEffect(() => {
    // Try to get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          console.log('📍 GPS detected:', { userLat, userLng });
          setUserLocation({ lat: userLat, lng: userLng });
          
          // ✅ ZOOM IN CLOSE to user location
          setViewState(prev => ({
            ...prev,
            longitude: userLng,
            latitude: userLat,
            zoom: 15 // Close zoom for GPS
          }));
          
          fetchNearbyVenues(userLat, userLng, 40000); // 40km on GPS lock — covers city + surroundings
          
          // ✅ FIX: Mark initial load complete after first fetch
          setTimeout(() => {
            setIsInitialLoad(false);
          }, 2000);
        },
        (error) => {
          console.log('📍 GPS not available, using default Phuket location');
          fetchNearbyVenues(REGION_CONFIG.mapCenter.latitude, REGION_CONFIG.mapCenter.longitude, 2000000); // no GPS = load all TH
          setTimeout(() => {
            setIsInitialLoad(false);
          }, 2000);
        }
      );
    } else {
      console.log('📍 No GPS support, using default location');
      fetchNearbyVenues(REGION_CONFIG.mapCenter.latitude, REGION_CONFIG.mapCenter.longitude, 2000000); // load all TH
      setTimeout(() => {
        setIsInitialLoad(false);
      }, 2000);
    }
  }, []); // ✅ FIX: Empty array = run ONCE only (prevents GPS jumping)

  // ========================================
  // LAZY LOADING (As User Pans to New Areas)
  // ========================================
  
  useEffect(() => {
    // ✅ FIX: Skip lazy loading during initial load
    if (!isMapLoaded || !mapRef.current || isInitialLoad) return;
    
    const map = mapRef.current.getMap();
    let timeout;
    
    const handleMoveEnd = () => {
      clearTimeout(timeout);
      // ✅ FIX: Increased debounce to avoid duplicate loads
      timeout = setTimeout(() => {
        const center = map.getCenter();
        const zoom = map.getZoom();
        
        // Zoom-adaptive radius — low zoom = large radius to cover whole country
        // zoom 15+: 3km, 13-14: 10km, 11-12: 40km, 9-10: 150km, <9: 2000km (all TH)
        const radius = zoom >= 15 ? 3000 : zoom >= 13 ? 10000 : zoom >= 11 ? 40000 : zoom >= 9 ? 150000 : 2000000;
        fetchNearbyVenues(center.lat, center.lng, radius);
      }, 1500); // ✅ FIX: Increased from 800ms
    };
    
    map.on('moveend', handleMoveEnd);
    
    return () => {
      map.off('moveend', handleMoveEnd);
      clearTimeout(timeout);
    };
  }, [isMapLoaded, fetchNearbyVenues, isInitialLoad]); // ✅ FIX: Add isInitialLoad dependency

  // ========================================
  // SAVE CACHE WHEN VENUES UPDATE
  // ========================================

  useEffect(() => {
    if (venues.length > 0) {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(venues));
        localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());
        localStorage.setItem(CACHE_AREAS_KEY, JSON.stringify([...loadedAreasRef.current]));
        console.log(`💾 Cached ${venues.length} venues (expires in 24hrs)`);
      } catch (error) {
        console.error('❌ Cache save failed:', error);
        // Clear partial cache if save failed
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_EXPIRY_KEY);
        localStorage.removeItem(CACHE_AREAS_KEY);
      }
    }
  }, [venues]);

  // ========================================
  // VISIBLE VENUES (in current viewport)
  // ========================================
  
  const visibleVenues = useMemo(() => {
    if (!mapRef.current || !isMapLoaded || venues.length === 0) return [];
    
    try {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      
      return venues.filter(venue => {
        const { lat, lng, isValid } = getVenueCoordinates(venue);
        if (!isValid) return false;
        
        return lat >= bounds._sw.lat && 
               lat <= bounds._ne.lat && 
               lng >= bounds._sw.lng && 
               lng <= bounds._ne.lng;
      }).slice(0, 50); // Limit to 50 for performance
    } catch (error) {
      return [];
    }
  }, [venues, viewState.latitude, viewState.longitude, viewState.zoom, isMapLoaded]); // ✅ FIXED: Added lat/lng so bounds update on pan

  // ========================================
  // NATIVE MAPBOX LAYER
  // ========================================
  
  const handleNativeVenueClick = useCallback((venueId) => {
    console.log('🎯 Venue clicked:', venueId);
    setSelectedVenue(venueId);
    
    // Navigate to venue detail page
    setTimeout(() => {
      navigate(`/venue/${venueId}`);
    }, 200);
  }, [navigate]);
  
  // Initialize native venue layer
  useNativeVenueLayer(
    mapRef,
    venues,
    isMapLoaded,
    handleNativeVenueClick
  );

  // ========================================
  // DIRECTIONAL COUNTS (Venues outside viewport)
  // ========================================
  
  const directionalVenueCounts = useMemo(() => {
    if (!mapRef.current || !isMapLoaded || !venues.length) {
      return { north: 0, south: 0, east: 0, west: 0, inViewport: 0 };
    }
    
    try {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      const centerLat = (bounds._sw.lat + bounds._ne.lat) / 2;
      const centerLng = (bounds._sw.lng + bounds._ne.lng) / 2;
      
      let north = 0, south = 0, east = 0, west = 0, inViewport = 0;
      
      venues.forEach(venue => {
        const { lat, lng, isValid } = getVenueCoordinates(venue);
        if (!isValid) return;
        
        const isInView = lat >= bounds._sw.lat && 
                         lat <= bounds._ne.lat && 
                         lng >= bounds._sw.lng && 
                         lng <= bounds._ne.lng;
        
        if (isInView) {
          inViewport++;
        } else {
          const latOffset = lat - centerLat;
          const lngOffset = lng - centerLng;
          
          if (Math.abs(latOffset) > Math.abs(lngOffset)) {
            if (lat > bounds._ne.lat) north++;
            else if (lat < bounds._sw.lat) south++;
          } else {
            if (lng > bounds._ne.lng) east++;
            else if (lng < bounds._sw.lng) west++;
          }
        }
      });
      
      return { north, south, east, west, inViewport };
    } catch (error) {
      return { north: 0, south: 0, east: 0, west: 0, inViewport: 0 };
    }
  }, [venues, Math.floor(viewState.zoom), isMapLoaded]);

  // ========================================
  // SEARCH (Uses Backend API)
  // ========================================
  
  const handleSearch = useCallback(async (query) => {
    if (!query || !query.trim()) {
      setSearchResults([]);
      return;
    }
    
    // ✅ PREVENT DUPLICATE SEARCHES
    if (isSearching) {
      console.log('⏭️ Search already in progress, skipping');
      return;
    }
    
    try {
      setIsSearching(true);
      const API_BASE_URL = getApiBaseUrl();
      
      // ✅ FIX: Get auth token for search API
      const token = localStorage.getItem('venue_app_token') || 
                    localStorage.getItem('token') || 
                    localStorage.getItem('authToken');
      
      // Include user location if available for distance calculation
      const lat = userLocation?.lat || viewState.latitude;
      const lng = userLocation?.lng || viewState.longitude;
      
      const url = `${API_BASE_URL}/venues/search?q=${encodeURIComponent(query)}&limit=20&lat=${lat}&lng=${lng}`;
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': REGION_CONFIG.region,
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('Search failed:', response.status);
        setSearchResults([]);
        return;
      }
      
      const data = await response.json();
      const searchVenues = data.venues || data.data || [];
      setSearchResults(searchVenues);

      console.log(`✅ Search found ${data.count} results for "${query}"`);

      // Auto-zoom to first result if exists
      if (searchVenues.length > 0 && mapRef.current) {
        const firstVenue = searchVenues[0];
        const { lat, lng, isValid } = getVenueCoordinates(firstVenue);
        
        if (isValid) {
          const map = mapRef.current.getMap();
          map.flyTo({
            center: [lng, lat],
            zoom: 16,
            duration: 1000
          });
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [userLocation, viewState.latitude, viewState.longitude, isSearching]); // ✅ Added isSearching to deps
  
  // Debounced search (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]); // ✅ FIX: Only searchQuery, not handleSearch (prevents infinite loop)

  // ========================================
  // HANDLERS
  // ========================================
  
  const handleMapLoad = useCallback(() => {
    console.log('✅ Map loaded');
    setIsMapLoaded(true);
    
    // ✅ HIDE OSM POI LABELS (hotels, landmarks, etc.)
    const map = mapRef.current?.getMap();
    if (map) {
      // Hide all POI labels from base map
      const layersToHide = [
        'poi-label',
        'transit-label', 
        'airport-label',
        'settlement-label',
        'settlement-subdivision-label'
      ];
      
      layersToHide.forEach(layerId => {
        try {
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', 'none');
          }
        } catch (e) {
          // Layer might not exist in current style
        }
      });
      
      console.log('🗺️ Hidden OSM POI labels - showing only our venue markers');
    }
  }, []);

  const handleMapMove = useCallback((evt) => {
    setViewState(evt.viewState);
  }, []);

  // ========================================
  // RENDER
  // ========================================
  
  return (
    <div style={{ height: '100vh', width: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Header */}
      <Header />
      
      {/* Search Bar - Top LEFT */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          position: 'absolute',
          top: '70px',
          left: '20px',
          zIndex: 10,
          width: '300px',
          maxWidth: 'calc(100vw - 180px)' // Leave space for right controls
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <MagnifyingGlassIcon style={{ width: 20, height: 20, color: '#6b7280', marginRight: '8px' }} />
          <input
            type="text"
            placeholder={t('map.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch(searchQuery);
              }
            }}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              backgroundColor: 'transparent'
            }}
          />
        </div>
      </motion.div>

      {/* Loading Indicator */}
      {isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: 'absolute',
            top: '140px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            fontSize: '13px',
            fontWeight: 600,
            color: '#374151'
          }}
        >
          {t('map.loadingVenues', { loaded: loadedCount, total: totalCount })}
        </motion.div>
      )}

      {/* Map */}
      <MapGL
        ref={mapRef}
        {...viewState}
        onMove={handleMapMove}
        onLoad={handleMapLoad}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={['venues-layer']}
      >
        {/* ✅ FIXED: GeolocateControl and Navigation on TOP RIGHT */}
        <GeolocateControl position="top-right" />
        <NavigationControl position="top-right" />
      </MapGL>

      {/* Directional Arrows */}
      <DirectionalArrow direction="north" count={directionalVenueCounts.north} mapRef={mapRef} />
      <DirectionalArrow direction="south" count={directionalVenueCounts.south} mapRef={mapRef} />
      <DirectionalArrow direction="east" count={directionalVenueCounts.east} mapRef={mapRef} />
      <DirectionalArrow direction="west" count={directionalVenueCounts.west} mapRef={mapRef} />
      
      {/* Venue Drawer */}
      <VenueDrawer
        venues={searchResults.length > 0 ? searchResults : visibleVenues}
        isLoading={isSearching}
        userLocation={userLocation}
        hasSearchQuery={searchQuery.trim().length > 0}
        searchQuery={searchQuery}
        totalCount={loadedCount}
      />


      {/* Venue Counter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          position: 'absolute',
          bottom: '100px',
          left: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          padding: '12px 16px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '13px',
          fontWeight: 600,
          color: '#374151',
          zIndex: 10
        }}
      >
        <span style={{ color: '#f59e0b', fontSize: '16px', marginRight: '4px' }}>
          {directionalVenueCounts.inViewport}
        </span>
        <span style={{ color: '#9ca3af', fontSize: '13px' }}>/ {loadedCount}</span>
        {totalCount > loadedCount && (
          <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '4px' }}>
            / {totalCount}
          </span>
        )}
      </motion.div>


      {/* Directional Arrows */}
      <DirectionalArrow direction="north" count={directionalVenueCounts.north} mapRef={mapRef} />
      <DirectionalArrow direction="south" count={directionalVenueCounts.south} mapRef={mapRef} />
      <DirectionalArrow direction="east" count={directionalVenueCounts.east} mapRef={mapRef} />
      <DirectionalArrow direction="west" count={directionalVenueCounts.west} mapRef={mapRef} />

    </div>
  );
};

export default VenueMap;
